import React, { useEffect, useRef } from "react"
import { Box, Typography } from "@material-ui/core"
import RoomIcon from "@material-ui/icons/Room"
import type { MarcadorPedido, MapaRoteirizacaoBaseProps } from "./tipos"
import { CENTRO_PADRAO, rotuloMarcador } from "./tipos"
import { CASA_SVG_DATA_URL, CASA_TAMANHO } from "./icones"
import { useMapboxGl } from "./useMapboxGl"

interface Props extends MapaRoteirizacaoBaseProps {
    token: string
}

function criarElementoPedido(m: MarcadorPedido): HTMLDivElement {
    const container = document.createElement("div")
    container.style.display = "flex"
    container.style.flexDirection = "column"
    container.style.alignItems = "center"
    container.style.cursor = "pointer"
    container.title = `#${m.shortReference} - ${m.cliente}${
        m.nomeEntregador ? ` · ${m.nomeEntregador}` : ""
    }`

    const rotulo = document.createElement("div")
    rotulo.textContent = rotuloMarcador(m)
    rotulo.style.fontSize = "11px"
    rotulo.style.fontWeight = "700"
    rotulo.style.lineHeight = "1"
    rotulo.style.color = "#1a1a1a"
    rotulo.style.background = "rgba(255,255,255,0.92)"
    rotulo.style.border = "1px solid rgba(0,0,0,0.2)"
    rotulo.style.borderRadius = "8px"
    rotulo.style.padding = "2px 5px"
    rotulo.style.marginBottom = "3px"
    rotulo.style.whiteSpace = "nowrap"
    rotulo.style.boxShadow = "0 1px 2px rgba(0,0,0,0.3)"

    const bola = document.createElement("div")
    const tamanho = m.selecionado ? 20 : 15
    bola.style.width = `${tamanho}px`
    bola.style.height = `${tamanho}px`
    bola.style.borderRadius = "50%"
    bola.style.background = m.cor
    bola.style.border = m.selecionado ? "3px solid #2e7d32" : "2px solid #ffffff"
    bola.style.boxShadow = "0 0 4px rgba(0,0,0,0.5)"

    container.appendChild(rotulo)
    container.appendChild(bola)
    return container
}

function criarElementoOrigem(): HTMLDivElement {
    const el = document.createElement("div")
    el.style.width = `${CASA_TAMANHO}px`
    el.style.height = `${CASA_TAMANHO}px`
    el.style.backgroundImage = `url("${CASA_SVG_DATA_URL}")`
    el.style.backgroundSize = "contain"
    el.style.backgroundRepeat = "no-repeat"
    el.style.backgroundPosition = "center"
    el.title = "Restaurante"
    return el
}

export function MapboxMapaRoteirizacao({ origem, marcadores, onSelecionarPedido, token }: Props) {
    const { loaded, erro } = useMapboxGl()
    const containerRef = useRef<HTMLDivElement | null>(null)
    const mapRef = useRef<any>(null)
    const markersRef = useRef<any[]>([])
    const idsAjustadosRef = useRef<string>("")

    // Mantém o handler mais recente sem recriar os marcadores.
    const onSelecionarRef = useRef(onSelecionarPedido)
    onSelecionarRef.current = onSelecionarPedido

    // Inicializa o mapa uma única vez.
    useEffect(() => {
        const mapboxgl = (window as any).mapboxgl
        if (!loaded || !mapboxgl || !containerRef.current || mapRef.current) return

        mapboxgl.accessToken = token
        const centro = origem ?? CENTRO_PADRAO
        mapRef.current = new mapboxgl.Map({
            container: containerRef.current,
            style: "mapbox://styles/mapbox/streets-v12",
            center: [centro.lng, centro.lat],
            zoom: origem ? 12 : 3,
        })
        mapRef.current.addControl(new mapboxgl.NavigationControl(), "top-right")

        return () => {
            if (mapRef.current) {
                mapRef.current.remove()
                mapRef.current = null
            }
        }
        // origem propositalmente fora das deps: só inicializa uma vez.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded, token])

    // Redesenha marcadores quando os dados ou a seleção mudam.
    useEffect(() => {
        const mapboxgl = (window as any).mapboxgl
        const map = mapRef.current
        if (!loaded || !mapboxgl || !map) return

        markersRef.current.forEach(mk => mk.remove())
        markersRef.current = []

        if (origem) {
            const mkOrigem = new mapboxgl.Marker({ element: criarElementoOrigem() })
                .setLngLat([origem.lng, origem.lat])
                .addTo(map)
            markersRef.current.push(mkOrigem)
        }

        marcadores.forEach(m => {
            const el = criarElementoPedido(m)
            el.addEventListener("click", ev => {
                ev.stopPropagation()
                onSelecionarRef.current(m.id)
            })
            const mk = new mapboxgl.Marker({ element: el, anchor: "bottom" })
                .setLngLat([m.longitude, m.latitude])
                .addTo(map)
            markersRef.current.push(mk)
        })

        // Reenquadra apenas quando o conjunto de pedidos muda (não a cada seleção).
        const idsKey = marcadores.map(m => m.id).join(",")
        if (idsKey !== idsAjustadosRef.current) {
            idsAjustadosRef.current = idsKey
            const pontos = marcadores.map(m => [m.longitude, m.latitude] as [number, number])
            if (origem) pontos.push([origem.lng, origem.lat])
            if (pontos.length === 1) {
                map.easeTo({ center: pontos[0], zoom: 14 })
            } else if (pontos.length > 1) {
                const bounds = new mapboxgl.LngLatBounds(pontos[0], pontos[0])
                pontos.forEach(p => bounds.extend(p))
                map.fitBounds(bounds, { padding: 60, maxZoom: 15, duration: 0 })
            }
        }
    }, [loaded, marcadores, origem])

    if (erro) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                p={3}
                style={{ textAlign: "center", color: "#7f8c8d" }}
            >
                <RoomIcon style={{ fontSize: 48, marginBottom: 8 }} />
                <Typography variant="body1">{erro}</Typography>
            </Box>
        )
    }

    return <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
}
